import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useQuery } from '@/components/_hooks/useQuery';
import { useToast } from '@/components/_common/Toast';
import { Exception } from '@/redux/slices/slice_types';
import { getCurrentException, setCurrentException } from '@/redux/slices/exceptionDrawerSlice';
import { selectCurrentException, selectExceptionDrawerLoading } from '@/redux/selectors/exceptionDrawerSelectors';

const useExceptionDrawer = () => {
  const { error } = useToast();
  const query = useQuery();
  const idQueryParam = query.get('id');
  const id = idQueryParam ? parseInt(idQueryParam) : undefined;
  const history = useHistory();
  const { search: filterParams, pathname } = useLocation();
  const dispatch = useAppDispatch();
  const exceptionLoading = useAppSelector(selectExceptionDrawerLoading);
  const activeException = useAppSelector(selectCurrentException);

  const setActiveException = (exception: Exception | null) => dispatch(setCurrentException(exception));

  const fetchException = async (id: Exception['id']) => {
    try {
      await dispatch(getCurrentException(id)).unwrap();
    } catch (ex: any) {
      error(ex.response || 'There was an error retrieving the exception.');
    }
  };

  const onDrawerClose = () => {
    setActiveException(null);
    const searchParams = new URLSearchParams(filterParams);
    searchParams.delete('id');
    history.replace({ search: '?' + searchParams.toString() });
  };

  const openExceptionInDrawer = (exception: Exception) => {
    const searchParams = new URLSearchParams(filterParams);
    searchParams.set('id', exception.id.toString());
    history.replace({ search: '?' + searchParams.toString() });
    setActiveException(exception);
  };

  useEffect(
    function handleFetchException() {
      if (id) {
        fetchException(id);
      }
    },
    [id]
  );

  useEffect(
    function handleSettingActiveException() {
      if (activeException?.id !== id && id) {
        fetchException(id);
      } else if (!id) {
        setActiveException(null);
      }
    },
    [pathname, id, activeException?.id]
  );

  return {
    activeException,
    openExceptionInDrawer,
    onDrawerClose,
    exceptionLoading
  };
};

export default useExceptionDrawer;
